import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import { useHookFormMask } from "use-mask-input";
import { ErrorForm } from "../../inter_register_user/ErrorForm.jsx";
import { useForm } from "react-hook-form";
import {
  AreaPreviewContainer,
  AreaPreviewCol,
  AreaPreviewContent,
  ExpandIcon,
  AreaNumber,
  AreaName,
  PreviewLabel,
  PreviewValue,
  SavedIndicator,
  StyledButton,
  ButtonContainer,
  Row,
  ShowInput,
  Label,
  StyledSelect,
  FormContainer,
  RightColumn,
  LeftColumn,
  HiddenFileInput,
  FileInputButton,
  ArquivoAtual,
  MainContainer,
  SideContainer,
  ContentWrapper,
  FormCard,
  FormHint,
} from "./styles";
import Sidebar from "../../sidebar";
import PageHeader from "../../page_header";
import { projectService } from "../../../services/projectService.js";
import { userService } from "../../../services/userService.js";
import { guardianAreaService } from "../../../services/guardianAreaService.js";

function normalizeProjectsPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.projects_project)) return payload.projects_project;
  if (Array.isArray(payload?.data?.projects_project)) {
    return payload.data.projects_project;
  }
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function normalizeUsersPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatBrazilianNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") return String(value);
  return String(value).replace(/\./g, "").replace(",", ".");
}

/**
 * Formulário de área Guardian.
 * - Standalone (`/create_area`): cria via POST /api/guardian/create_area/ com projeto obrigatório.
 * - Aninhado (cadastro/edição de projeto): chama onAreaChange; projeto vem de projectId.
 */
const CreateArea = ({
  areaNumber,
  onAreaChange,
  onRemove,
  initialData = null,
  isEdit = false,
  projectId = null,
}) => {
  const isStandalone = typeof onAreaChange !== "function";
  const navigate = useNavigate();
  const collapsed = useSelector((state) => state.sidebar);
  const { showError, showSuccess, showWarning } = useModalContext();
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [otherResponsibles, setOtherResponsibles] = useState([]);
  const [adiImage, setAdiImage] = useState(null);
  const [isSaved, setIsSaved] = useState(isEdit);
  const [isExpanded, setIsExpanded] = useState(!isEdit);
  const [savedData, setSavedData] = useState(initialData || null);
  const [options, setOptions] = useState({
    projects: [],
    users: [],
  });

  const resolvedProjectDefault =
    initialData?.project_detail?.id ??
    initialData?.project ??
    projectId ??
    "";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || "",
      project: resolvedProjectDefault ? String(resolvedProjectDefault) : "",
      vert_responsible: initialData?.vert_responsible || "",
      adi_responsible: initialData?.adi_responsible || "",
      matricula_status: initialData?.matricula_status || "",
      total_area: initialData?.total_area || "",
      legal_reserve_area: initialData?.legal_reserve_area || "",
      reserve_legal_status: initialData?.reserve_legal_status || "",
      georeferencing_status: initialData?.georeferencing_status || "",
      status_car: initialData?.status_car || "",
      sicar_code: initialData?.sicar_code || "",
    },
  });

  const registerWithMask = useHookFormMask(register);

  useEffect(() => {
    if (isEdit && initialData) {
      if (initialData.other_responsibles) {
        setOtherResponsibles(
          initialData.other_responsibles.map((r) =>
            typeof r === "object" ? r.id : r
          )
        );
      }
      if (initialData.adi_image && typeof initialData.adi_image === "string") {
        setAdiImage({
          name: initialData.adi_image.split("/").pop(),
          url: initialData.adi_image,
        });
      }
    }
  }, [isEdit, initialData]);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const [projectsRes, usersRes] = await Promise.all([
          projectService.getProjects().catch((err) => {
            console.warn("Projetos indisponíveis no formulário de área:", err);
            return [];
          }),
          userService.getUsers().catch((err) => {
            console.warn("Usuários indisponíveis no formulário de área:", err);
            return [];
          }),
        ]);

        setOptions({
          projects: normalizeProjectsPayload(projectsRes),
          users: normalizeUsersPayload(usersRes),
        });
        setLoadError(null);
      } catch (err) {
        console.error("Erro ao carregar opções:", err);
        setLoadError("Erro ao carregar dados do formulário");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  const buildPayload = (data) => {
    const project =
      data.project || projectId || resolvedProjectDefault || null;

    const payload = {
      name: data.name?.trim(),
      project,
      matricula_status: data.matricula_status || "",
      reserve_legal_status: data.reserve_legal_status || "",
      georeferencing_status: data.georeferencing_status || "",
      status_car: data.status_car || "",
      sicar_code: data.sicar_code || "",
      other_responsibles: otherResponsibles,
    };

    if (data.vert_responsible) {
      payload.vert_responsible = parseInt(data.vert_responsible, 10);
    }
    if (data.adi_responsible) {
      payload.adi_responsible = parseInt(data.adi_responsible, 10);
    }

    const total = formatBrazilianNumber(data.total_area);
    const legal = formatBrazilianNumber(data.legal_reserve_area);
    if (total !== "") payload.total_area = total;
    if (legal !== "") payload.legal_reserve_area = legal;

    if (adiImage instanceof File) {
      payload.adi_image = adiImage;
    }

    return payload;
  };

  const processFormData = async (data) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const projectValue = data.project || projectId || resolvedProjectDefault;
      if (!data.name?.trim()) {
        showError("Erro!", "Informe o nome da área.");
        return;
      }
      if (!projectValue) {
        showError(
          "Erro!",
          "Selecione o projeto ao qual esta área pertence."
        );
        return;
      }

      const payload = buildPayload(data);

      if (isStandalone) {
        const result = await guardianAreaService.createGuardianArea(payload);
        await showSuccess("Sucesso!", "Área cadastrada com sucesso!");
        const idCode = result?.id_code || result?.id;
        if (idCode) {
          navigate(`/area_details/${idCode}`);
        } else {
          navigate("/areas");
        }
        return;
      }

      const areaData = {
        ...payload,
        ...data,
        areaNumber,
        area_number: areaNumber,
        number: areaNumber,
        other_responsibles: otherResponsibles,
        adi_image: adiImage,
        id: initialData?.id,
        id_code: initialData?.id_code,
        project: payload.project,
        isEdit,
      };

      const areaId = initialData?.id || `temp-${Date.now()}`;
      await Promise.resolve(onAreaChange(areaId, areaData));

      setSavedData(areaData);
      setIsSaved(true);
      setIsExpanded(false);
      showSuccess(
        "Sucesso!",
        `Área ${isEdit ? "atualizada" : "salva"} com sucesso!`
      );
    } catch (error) {
      console.error("Erro ao salvar área:", error);
      const detail =
        error.response?.data?.detail ||
        error.response?.data?.name?.[0] ||
        error.response?.data?.project?.[0] ||
        error.message ||
        `Erro ao ${isEdit ? "atualizar" : "salvar"} área.`;
      showError("Erro!", String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const registerAreaField = (name) => {
    const registered = register(name);
    return {
      ...registered,
      onChange: (e) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value === "") {
          e.target.value = "";
          registered.onChange(e);
          return;
        }
        value = value.padStart(3, "0");
        const integerPart = value.slice(0, -2);
        const decimalPart = value.slice(-2);
        const integerFormatted = Number(integerPart).toLocaleString("pt-BR");
        e.target.value = `${integerFormatted},${decimalPart}`;
        registered.onChange(e);
      },
    };
  };

  const handleCancel = (e) => {
    e.preventDefault();
    if (isStandalone) {
      navigate("/areas");
      return;
    }
    if (!isSaved) {
      const confirm = window.confirm(
        "Tem certeza que deseja remover esta área? Os dados não salvos serão perdidos."
      );
      if (confirm) onRemove?.();
    } else {
      onRemove?.();
    }
  };

  const addOtherResponsible = (userId) => {
    if (!userId) return;
    if (otherResponsibles.includes(userId)) return;
    if (otherResponsibles.length < 5) {
      setOtherResponsibles((prev) => [...prev, userId]);
    } else {
      showWarning("Limite atingido", "Você só pode selecionar até 5 usuários.");
    }
  };

  const removeOtherResponsible = (userId) => {
    setOtherResponsibles((prev) => prev.filter((id) => id !== userId));
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const getUserName = (userId) => {
    const user = (options.users || []).find(
      (u) => u.id === userId || String(u.id) === String(userId)
    );
    return user ? user.full_name : "Usuário";
  };

  const formatAreaValue = (value) => {
    if (!value) return "Não informado";
    return `${value} ha`;
  };

  const projectSelect = (
    <Row>
      <Label htmlFor={`project-${areaNumber ?? "new"}`}>
        Projeto {isStandalone ? "(obrigatório)" : ""}
      </Label>
      <StyledSelect
        id={`project-${areaNumber ?? "new"}`}
        disabled={
          loadingOptions ||
          (!isStandalone && Boolean(projectId)) ||
          options.projects.length === 0
        }
        {...register("project")}
      >
        <option value="">
          {loadingOptions
            ? "Carregando projetos..."
            : options.projects.length === 0
              ? "Nenhum projeto disponível"
              : "Selecione o projeto desta área"}
        </option>
        {options.projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.title || project.name || `Projeto #${project.id}`}
          </option>
        ))}
      </StyledSelect>
      <ErrorForm message={errors.project?.message} />
    </Row>
  );

  const formFields = (
    <>
      <LeftColumn>
        {projectSelect}

        <Row>
          <Label>Nome da Área:</Label>
          <ShowInput
            type="text"
            id="name"
            placeholder="Digite o nome da área"
            {...register("name")}
          />
          <ErrorForm message={errors.name?.message} />
        </Row>

        <Row>
          <Label>Status da Matrícula</Label>
          <StyledSelect id="matricula_status" {...register("matricula_status")}>
            <option value="">Selecione uma opção</option>
            <option value="Vigente">Vigente</option>
            <option value="Em atualização">Em atualização</option>
            <option value="Cancelada">Cancelada</option>
          </StyledSelect>
        </Row>

        <Row>
          <Label>Área total da propriedade (ha)?</Label>
          <ShowInput
            type="text"
            id="total_area"
            {...registerAreaField("total_area")}
            placeholder="Em hectares (ha)"
          />
        </Row>

        <Row>
          <Label>Área total da reserva legal (ha)?</Label>
          <ShowInput
            type="text"
            id="legal_reserve_area"
            {...registerAreaField("legal_reserve_area")}
            placeholder="Em hectares (ha)"
          />
        </Row>

        <Row>
          <Label>Situação da reserva legal da propriedade:</Label>
          <StyledSelect
            id="reserve_legal_status"
            {...register("reserve_legal_status")}
          >
            <option value="">Selecione uma opção</option>
            <option value="Sem vegetação / em regeneração natural">
              Sem vegetação / em regeneração natural
            </option>
            <option value="Em regeneração a partir de reflorestamento">
              Em regeneração a partir de reflorestamento
            </option>
            <option value="Parcialmente florestada / em regeneração natural">
              Parcialmente florestada / em regeneração natural
            </option>
            <option value="Completamente florestada">
              Completamente florestada
            </option>
          </StyledSelect>
        </Row>

        <Row>
          <Label>Status do georreferenciamento no SIGEF</Label>
          <StyledSelect
            id="georeferencing_status"
            {...register("georeferencing_status")}
          >
            <option value="">Selecione uma opção</option>
            <option value="Atualizado">Atualizado</option>
            <option value="Em atualização">Em atualização</option>
            <option value="Pendente">Pendente</option>
            <option value="Não aplicável">Não aplicável</option>
          </StyledSelect>
        </Row>
      </LeftColumn>

      <RightColumn>
        <Row>
          <Label>Status do CAR</Label>
          <StyledSelect id="status_car" {...register("status_car")}>
            <option value="">Selecionar opção</option>
            <option value="Ativo">Ativo</option>
            <option value="Pendente">Pendente</option>
            <option value="Cancelado">Cancelado</option>
            <option value="Não possui CAR">Não possui CAR</option>
          </StyledSelect>
        </Row>

        <Row>
          <Label>Código SICAR (CAR)</Label>
          <ShowInput
            type="text"
            id="sicar_code"
            placeholder="Ex: 12-3456789-1234.1234.1234.1234.1234.1234.1234.1234"
            {...registerWithMask(
              "sicar_code",
              "**-*******-****.****.****.****.****.****.****.****"
            )}
          />
        </Row>

        <Row>
          <Label>Responsável VERT:</Label>
          <StyledSelect
            id="vert_responsible"
            {...register("vert_responsible")}
          >
            <option value="">
              {options.users.length === 0
                ? "Usará o usuário logado (padrão)"
                : "Selecione o usuário (opcional)"}
            </option>
            {(options.users || [])
              .filter(
                (user) => user.user_type === "ADM" || user.user_type === "ENG"
              )
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
          </StyledSelect>
        </Row>

        <Row>
          <Label>Gestor:</Label>
          <StyledSelect id="adi_responsible" {...register("adi_responsible")}>
            <option value="">Selecione o usuário</option>
            {(options.users || [])
              .filter(
                (user) =>
                  user.user_type === "GUARDIAN" || user.user_type === "PROP"
              )
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
          </StyledSelect>
        </Row>

        <Row>
          <Label>Equipe Responsável (Membros):</Label>
          <StyledSelect
            id="other_responsibles"
            value=""
            onChange={(e) => {
              const selectedId = Number(e.target.value);
              addOtherResponsible(selectedId);
              e.target.value = "";
            }}
          >
            <option value="">Selecione um usuário</option>
            {(options.users || [])
              .filter(
                (user) =>
                  (user.user_type === "GUARDIAN" ||
                    user.user_type === "PROP") &&
                  !otherResponsibles.includes(user.id)
              )
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
          </StyledSelect>

          {otherResponsibles.length > 0 && (
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
              }}
            >
              {otherResponsibles.map((id, index) => {
                const user = (options.users || []).find((u) => u?.id === id);
                return (
                  <div
                    key={`selected-user-${areaNumber}-${id}-${index}`}
                    style={{
                      background: "var(--surface-2)",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {user?.full_name || "Usuário"}
                    <span
                      onClick={() => removeOtherResponsible(id)}
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      ×
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Row>

        <Row>
          <Label>Imagem ADI:</Label>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              width: "100%",
              marginTop: "0.25rem",
            }}
          >
            <FileInputButton
              htmlFor={`file-input-adi-${areaNumber ?? "new"}`}
              style={{
                width: "100%",
                maxWidth: "300px",
                padding: "0.5rem 1rem",
              }}
            >
              📁 Selecionar Arquivo
            </FileInputButton>

            <HiddenFileInput
              id={`file-input-adi-${areaNumber ?? "new"}`}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) setAdiImage(file);
              }}
            />
            {adiImage && (
              <ArquivoAtual style={{ marginBottom: "0.25rem" }}>
                {adiImage.url ? (
                  <>
                    Arquivo atual:{" "}
                    <a
                      href={adiImage.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "var(--accent)",
                        textDecoration: "underline",
                      }}
                    >
                      {adiImage.name}
                    </a>
                  </>
                ) : (
                  `Arquivo atual: ${adiImage.name}`
                )}
              </ArquivoAtual>
            )}
          </div>
        </Row>
      </RightColumn>
    </>
  );

  const actionButtons = (
    <ButtonContainer>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <StyledButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit((formData) => {
              processFormData(formData);
            })(e);
          }}
          disabled={submitting}
        >
          {submitting
            ? "Salvando..."
            : isStandalone
              ? "Cadastrar área"
              : isEdit
                ? "Atualizar Área"
                : "Salvar Área"}
        </StyledButton>

        <StyledButton
          type="button"
          onClick={handleCancel}
          disabled={submitting}
          style={{
            background:
              "color-mix(in srgb, var(--danger) 80%, transparent)",
            borderColor:
              "color-mix(in srgb, var(--danger) 50%, transparent)",
          }}
        >
          {isStandalone ? "Cancelar" : isEdit ? "Excluir Área" : "Remover Área"}
        </StyledButton>

        {!isStandalone && isSaved && (
          <StyledButton
            type="button"
            onClick={toggleExpand}
            style={{
              background: "var(--surface-3)",
              borderColor: "var(--border-subtle)",
            }}
          >
            {isExpanded ? "Fechar Visualização" : "Abrir Visualização"}
          </StyledButton>
        )}
      </div>
    </ButtonContainer>
  );

  if (loadError && options.projects.length === 0 && !loadingOptions) {
    const errorBlock = (
      <div style={{ padding: "20px", color: "var(--danger)" }}>
        Erro: {loadError}
      </div>
    );
    if (!isStandalone) return errorBlock;
    return (
      <MainContainer>
        <Sidebar />
        <SideContainer collapsed={collapsed}>{errorBlock}</SideContainer>
      </MainContainer>
    );
  }

  if (!isStandalone && isSaved && !isExpanded) {
    return (
      <AreaPreviewContainer onClick={toggleExpand}>
        <AreaPreviewContent>
          <AreaPreviewCol flex={1.5}>
            <ExpandIcon>{isExpanded ? "▲" : "▼"}</ExpandIcon>
            <AreaNumber>Área {areaNumber}</AreaNumber>
            <AreaName>{savedData?.name || "Sem nome"}</AreaName>
          </AreaPreviewCol>

          <AreaPreviewCol flex={1.2}>
            <PreviewLabel>VERT:</PreviewLabel>
            <PreviewValue>
              {getUserName(savedData?.vert_responsible)?.split(" ")[0] || "-"}
            </PreviewValue>
          </AreaPreviewCol>

          <AreaPreviewCol flex={1}>
            <PreviewLabel>Gestor:</PreviewLabel>
            <PreviewValue>
              {savedData?.adi_responsible
                ? getUserName(savedData.adi_responsible)?.split(" ")[0] || "-"
                : "-"}
            </PreviewValue>
          </AreaPreviewCol>

          <AreaPreviewCol flex={1}>
            <PreviewLabel>Área:</PreviewLabel>
            <PreviewValue>{formatAreaValue(savedData?.total_area)}</PreviewValue>
          </AreaPreviewCol>

          <AreaPreviewCol flex={1}>
            <PreviewLabel>CAR:</PreviewLabel>
            <PreviewValue>
              {savedData?.status_car
                ? savedData.status_car === "Ativo"
                  ? "Ativo"
                  : savedData.status_car === "Pendente"
                    ? "Pend."
                    : savedData.status_car === "Cancelado"
                      ? "Canc."
                      : "N/A"
                : "N/A"}
            </PreviewValue>
          </AreaPreviewCol>

          <AreaPreviewCol flex={0.8}>
            <SavedIndicator>
              {isEdit ? "✓ Editada" : "✓ Salva"}
            </SavedIndicator>
          </AreaPreviewCol>
        </AreaPreviewContent>
      </AreaPreviewContainer>
    );
  }

  if (isStandalone) {
    return (
      <MainContainer>
        <Sidebar />
        <SideContainer collapsed={collapsed}>
          <ContentWrapper>
            <PageHeader
              title="Nova área"
              actionLabel="Voltar"
              onAction={() => navigate("/areas")}
            />
            <FormCard>
              <FormHint>
                Cadastre uma área Guardian vinculada a um projeto existente.
                O campo <strong>Projeto</strong> é obrigatório — a área passa a
                constar na listagem desse projeto.
              </FormHint>
              <FormContainer as="form" onSubmit={(e) => e.preventDefault()}>
                {formFields}
                {actionButtons}
              </FormContainer>
            </FormCard>
          </ContentWrapper>
        </SideContainer>
      </MainContainer>
    );
  }

  return (
    <FormContainer>
      {formFields}
      {actionButtons}
    </FormContainer>
  );
};

export default CreateArea;
